import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { DateRangePicker } from 'react-dates';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ReactComponent as RightArrow } from '@assets/icons/rightArrow.svg';
import { ReactComponent as LeftArrow } from '@assets/icons/leftArrow.svg';

import useClickOutside from '@hooks/useClickOutside';
import useWindowSize from '@hooks/useWindowSize';

import { filterFieldType } from '@features/filter/static/filterUtil';

import { storeSelectedFilterCheckboxType, updateTextItemInGlobalFilter } from '@store/actions/FilterAction';

import ApiClient from '@lib/apiClient';

import { changeDateFormat, defaultDateRangePresets, getDateRange } from '@utils/Util';

import CommonImage from '@/components/CommonImage';

const DateRangeRightSidePanel = ({
	type,
	presets = defaultDateRangePresets,
	clearDate,
	className = 'left-content',
	isCloseOnOutsideClick = true,
	activeLayer = ''
}) => {
	const selectedField = useSelector((store) => store.slectedCheckBoxFieldStore);
	const filterData = useSelector((store) => store.filterFieldStore);
	const [selectedId, setSelectedId] = useState('date-range-picker-input-1');
	const [focusedInput, setFocusedInput] = useState('startDate');
	const [orientation, setOrientation] = useState('horizontal');
	const [isShowCalendar] = useState(true);
	const [fromDate, setFromDate] = useState(null);
	const [toDate, setToDate] = useState(null);
	const [manualStartDate, setManualStartDate] = useState('');
	const [manualEndDate, setManualEndDate] = useState('');
	const dispatch = useDispatch();
	const leftPanelRef = useRef();
	useWindowSize(() => setOrientation('vertical'));
	const location = useLocation();

	useEffect(() => {
		let selectedFiledName = filterFieldType[type]['idName'] || null;
		let selectedData = filterData[selectedFiledName] && filterData[selectedFiledName][0];
		if (selectedData?.key) {
			let splitDate = selectedData?.key?.split(',');
			setSelectedId(selectedData?.additionalData);
			setFromDate(splitDate[0]);
			setToDate(splitDate[1]);
		} else {
			generateDateIntervals('today');
		}
	}, [filterData]);

	const onOutSideClick = () => {
		if (isCloseOnOutsideClick) dispatch(storeSelectedFilterCheckboxType(null));
	};

	useClickOutside(leftPanelRef, onOutSideClick);

	useEffect(() => {
		setManualStartDate(fromDate ? moment(fromDate, 'YYYY-MM-DD').format('DD-MM-YYYY') : '');
		setManualEndDate(toDate ? moment(toDate, 'YYYY-MM-DD').format('DD-MM-YYYY') : '');
	}, [fromDate, toDate]);

	const saveDate = async (startDate, endDate) => {
		let from = startDate ? startDate.format('YYYY-MM-DD') : null;
		let to = endDate ? endDate.format('YYYY-MM-DD') : null;
		setFromDate(from);
		setToDate(to);
	};

	const generateDateIntervals = async (interval) => {
		let { start, end } = getDateRange(interval);
		if (start) setFromDate(moment(start).format('YYYY-MM-DD'));
		if (end) setToDate(moment(end).format('YYYY-MM-DD'));
	};

	const setSelectedInputId = (e, id) => {
		e.stopPropagation();
		setSelectedId(id);
	};

	const handleClose = () => {
		dispatch(storeSelectedFilterCheckboxType(null));
	};
	const handleApply = () => {
		let data = {
			name: activeLayer ? activeLayer : filterFieldType[type]['idName'],
			key: fromDate + ',' + toDate,
			value:
				changeDateFormat(fromDate, 'YYYY-MM-DD', 'DD MMM, YYYY') +
				(toDate ? ' - ' + changeDateFormat(toDate, 'YYYY-MM-DD', 'DD MMM, YYYY') : ''),
			identifier: 'dateRange',
			additionalData: selectedId,
			activeLayer: activeLayer
		};
		dispatch(updateTextItemInGlobalFilter(data));
		if (location.pathname === '/trends') {
			ApiClient.POST('mixpanelAddEvent', { filterType: 'dateRange' }, 'TRENDS_APPLY_FILTER');
		}
		handleClose();
	};

	const applyManualDateRange = () => {
		const trimmedStartValue = manualStartDate.trim();
		const trimmedEndValue = manualEndDate.trim();
		if (!trimmedStartValue || !trimmedEndValue) {
			return;
		}

		const startDate = moment(trimmedStartValue, 'DD-MM-YYYY', true);
		const endDate = moment(trimmedEndValue, 'DD-MM-YYYY', true);

		if (startDate.isValid() && endDate.isValid()) {
			setFromDate(startDate.format('YYYY-MM-DD'));
			setToDate(endDate.format('YYYY-MM-DD'));
		}
	};

	const renderCalendarInfo = () => {
		return (
			<div className="calendar-footer d-flex justify-content-between p-3 border-top-solid">
				<button className="button size32 outline mr-12 clear-button" onClick={clearDate}>
					Clear
				</button>
				<div className="all-button d-flex">
					<p className="pt-1 mr-2">
						{fromDate && changeDateFormat(fromDate, 'YYYY-MM-DD', 'DD MMM, YYYY')}
						{toDate ? ' - ' + changeDateFormat(toDate, 'YYYY-MM-DD', 'DD MMM, YYYY') : ''}
					</p>
					<button className="button gray-outline size32 mr-12" onClick={handleClose}>
						Cancel
					</button>
					<button className="button size32" onClick={handleApply}>
						Apply
					</button>
				</div>
			</div>
		);
	};

	return (
		selectedField?.selectedCheckboxField === type && (
			<div ref={leftPanelRef} className="topbar-date-range-filter">
				<div className={`filter-submenu ${className}`}>
					<div className="card border-0">
						<div className="card-body date-options">
							<div className="form-group">
								<h3 className="semibold-14 pt-3 px-3">Date Presets</h3>
							</div>
							{presets.map(({ id, interval, label }) => (
								<div
									key={id}
									className={`form-group cursor-pointer ${
										selectedId === id ? 'active' : ''
									}`}
								>
									<span
										className="regular-14 gray_dark_01"
										onClick={(e) => {
											generateDateIntervals(interval);
											setSelectedInputId(e, id);
										}}
									>
										{label}
									</span>
								</div>
							))}
						</div>
					</div>
				</div>
				{isShowCalendar && (
					<div className="filter-submenu submenu-date right-content">
						<div className="card border-0">
							<div className="card-body">
								<div className="calender-and-input d-flex gap-2 align-items-center bg-white">
									<input
										type="search"
										className="manual-date-range-input w-50 regular-14"
										placeholder="Start Date (DD-MM-YYYY)"
										value={manualStartDate}
										onChange={(e) => setManualStartDate(e.target.value)}
										onBlur={applyManualDateRange}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												applyManualDateRange();
											}
										}}
									/>
									<span className="icon">
										<CommonImage src={'/icons/arrow-back.svg'} width={16} height={16} />
									</span>
									<input
										type="search"
										className="manual-date-range-input w-50 regular-14"
										placeholder="End Date (DD-MM-YYYY)"
										value={manualEndDate}
										onChange={(e) => setManualEndDate(e.target.value)}
										onBlur={applyManualDateRange}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												applyManualDateRange();
											}
										}}
									/>
								</div>

								<div className="common-date-to-date-calenter">
									<DateRangePicker
										daySize={30}
										numberOfMonths={2}
										minimumNights={0}
										displayFormat="MMM DD"
										isOutsideRange={() => false}
										startDate={fromDate ? moment(fromDate, 'YYYY-MM-DD') : null}
										startDateId="start_date_id"
										endDate={toDate ? moment(toDate, 'YYYY-MM-DD') : null}
										endDateId="end_date_id"
										onDatesChange={({ startDate, endDate }) =>
											saveDate(startDate, endDate)
										}
										focusedInput={focusedInput}
										onFocusChange={(newFocusedInput) => {
											if (newFocusedInput) {
												setFocusedInput(newFocusedInput);
											}
										}}
										keepOpenOnDateSelect={true}
										navPrev={<LeftArrow />}
										navNext={<RightArrow />}
										hideKeyboardShortcutsPanel={true}
										renderCalendarInfo={renderCalendarInfo}
										orientation={orientation}
									/>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		)
	);
};

export default DateRangeRightSidePanel;
